/**
 * Per-account settings for features, resolved to the signed-in account.
 *
 * A thin face over the store's `meta` object store so a feature never names
 * an account itself. Never throws; signed out, reads miss and writes report
 * false.
 */

import { currentAccountId } from './account';
import { captureScope, getMeta, setMeta, withStores } from './store';

import type { WriteScope } from './store';

/** Read a setting for the signed-in account; null on a miss or signed out. */
export async function readMeta<T>(key: string): Promise<T | null> {
  const account = currentAccountId();
  return account ? getMeta<T>(key, account) : null;
}

/** Write a setting for the signed-in account; true only when it landed. */
export async function writeMeta<T>(key: string, value: T): Promise<boolean> {
  const account = currentAccountId();
  return account ? setMeta(key, value, account) : false;
}

/**
 * Remove a setting for the signed-in account.
 *
 * Writes `null` rather than deleting the row: `getMeta` already reads a null
 * value as a miss (`?? null`), so this needs no new store function.
 */
export async function clearMeta(key: string): Promise<void> {
  const account = currentAccountId();
  if (!account) return;
  await setMeta(key, null, account);
}

/** A fresh random id; `randomUUID` where the browser has it, a timestamp otherwise. */
const randomId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** The id minted (or read) for one store, keyed by account and generation. */
let minted: { key: string; id: Promise<string> } | null = null;

/** Read the stored id or store a fresh one, in one transaction, so two tabs cannot mint two. */
async function mintDeviceId(scope: WriteScope | null): Promise<string> {
  const fresh = randomId();
  if (!scope) return fresh;
  const stored = await withStores(scope, 'readwrite', async tx => {
    const meta = tx.objectStore('meta');
    const existing = (await meta.get('deviceId'))?.value as string | undefined;
    if (existing) return existing;
    await meta.put({ key: 'deviceId', value: fresh });
    return fresh;
  }).catch(() => null);
  return stored ?? fresh;
}

/**
 * A stable random id for this store, minted on first use; salts ids a device
 * generates so two devices conflicting at the same revision stay apart.
 *
 * One mint at a time in this tab, and one transaction across tabs: two
 * first-use callers must not race to two ids. A store that refuses the write
 * still gets one id for the life of this tab. A new store (another account,
 * or the same one after a wipe) gets its own.
 */
export function deviceId(): Promise<string> {
  const scope = captureScope();
  const key = scope ? `${scope.account}#${scope.generation}` : '';
  if (minted?.key === key) return minted.id;
  const id = mintDeviceId(scope);
  minted = { key, id };
  return id;
}

/** Tests start with no id remembered in this tab. */
export function resetDeviceIdForTests(): void {
  minted = null;
}
